import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { householdsApi, invitationsApi, simplefinApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Users, Copy, UserPlus, LogOut, Crown, Trash2, RefreshCw, Mail, Check, X, Clock, UserCheck, UserX, CreditCard, CheckCircle2 } from 'lucide-react'
import type { Household, HouseholdMember, HouseholdInvitation } from '@/types'

export function HouseholdSettings() {
  const queryClient = useQueryClient()
  const [householdName, setHouseholdName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<HouseholdMember | null>(null)
  const [simplefinAccessUrl, setSimplefinAccessUrl] = useState('')

  // Fetch current household
  const { data: household, isLoading: loadingHousehold } = useQuery<Household | null>({
    queryKey: ['household'],
    queryFn: async () => {
      const res = await householdsApi.getMine()
      return res.data
    },
  })

  // Fetch members if in a household
  const { data: members = [] } = useQuery<HouseholdMember[]>({
    queryKey: ['household-members', household?.id],
    queryFn: async () => {
      if (!household?.id) return []
      const res = await householdsApi.getMembers(household.id)
      return res.data
    },
    enabled: !!household?.id,
  })

  // Fetch sent invitations (for admins)
  const { data: sentInvitations = [] } = useQuery<HouseholdInvitation[]>({
    queryKey: ['household-invitations', household?.id],
    queryFn: async () => {
      if (!household?.id || household.role !== 'admin') return []
      const res = await householdsApi.getInvitations(household.id)
      return res.data
    },
    enabled: !!household?.id && household?.role === 'admin',
  })

  // Fetch pending members (for admins)
  const { data: pendingMembers = [] } = useQuery<any[]>({
    queryKey: ['household-pending-members', household?.id],
    queryFn: async () => {
      if (!household?.id || household.role !== 'admin') return []
      const res = await householdsApi.getPendingMembers(household.id)
      return res.data
    },
    enabled: !!household?.id && household?.role === 'admin',
  })

  // Fetch pending invitations for this user
  const { data: pendingInvitations = [] } = useQuery<HouseholdInvitation[]>({
    queryKey: ['pending-invitations'],
    queryFn: async () => {
      const res = await invitationsApi.getPending()
      return res.data
    },
  })

  // Fetch SimpleFIN config for household (admin only)
  const { data: simplefinConfig } = useQuery<{ configured: boolean; active: boolean; lastSynced: string | null }>({
    queryKey: ['simplefin-config', household?.id],
    queryFn: async () => {
      const res = await simplefinApi.getConfig()
      return res.data
    },
    enabled: !!household?.id && household?.role === 'admin',
  })

  // Create household mutation
  const createHousehold = useMutation({
    mutationFn: (name: string) => householdsApi.create(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] })
      setHouseholdName('')
      alert('Household created!')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to create household')
    },
  })

  // Join household mutation
  const joinHousehold = useMutation({
    mutationFn: (code: string) => householdsApi.joinByCode(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] })
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] })
      setJoinCode('')
      alert('Joined household!')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to join household')
    },
  })

  // Invite member mutation
  const inviteMember = useMutation({
    mutationFn: (email: string) => householdsApi.invite(household!.id, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household-invitations'] })
      setInviteEmail('')
      alert('Invitation sent!')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to send invitation')
    },
  })

  // Leave household mutation
  const leaveHousehold = useMutation({
    mutationFn: () => householdsApi.leave(household!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] })
      queryClient.invalidateQueries({ queryKey: ['household-members'] })
      alert('Left household')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to leave household')
    },
  })

  // Delete household mutation
  const deleteHousehold = useMutation({
    mutationFn: () => householdsApi.delete(household!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] })
      alert('Household deleted')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to delete household')
    },
  })

  // Remove member mutation
  const removeMember = useMutation({
    mutationFn: (userId: number) => householdsApi.removeMember(household!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household-members'] })
      setMemberToRemove(null)
      alert('Member removed')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to remove member')
    },
  })

  // Regenerate invite code mutation
  const regenerateCode = useMutation({
    mutationFn: () => householdsApi.regenerateCode(household!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] })
      alert('Invite code regenerated')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to regenerate code')
    },
  })

  // Accept invitation mutation
  const acceptInvitation = useMutation({
    mutationFn: (id: number) => invitationsApi.accept(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] })
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] })
      alert('Invitation accepted!')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to accept invitation')
    },
  })

  // Decline invitation mutation
  const declineInvitation = useMutation({
    mutationFn: (id: number) => invitationsApi.decline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] })
      alert('Invitation declined')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to decline invitation')
    },
  })

  // Cancel invitation mutation
  const cancelInvitation = useMutation({
    mutationFn: (inviteId: number) => householdsApi.cancelInvitation(household!.id, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household-invitations'] })
      alert('Invitation cancelled')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to cancel invitation')
    },
  })

  // Approve member mutation
  const approveMember = useMutation({
    mutationFn: (userId: number) => householdsApi.approveMember(household!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household-pending-members'] })
      queryClient.invalidateQueries({ queryKey: ['household-members'] })
      alert('Member approved!')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to approve member')
    },
  })

  // Reject member mutation
  const rejectMember = useMutation({
    mutationFn: (userId: number) => householdsApi.rejectMember(household!.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household-pending-members'] })
      alert('Member rejected')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to reject member')
    },
  })

  // SimpleFIN config mutations
  const setSimplefinConfig = useMutation({
    mutationFn: (accessUrl: string) => simplefinApi.setConfig(accessUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simplefin-config'] })
      setSimplefinAccessUrl('')
      alert('SimpleFIN configured successfully!')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to configure SimpleFIN')
    },
  })

  const deleteSimplefinConfig = useMutation({
    mutationFn: () => simplefinApi.deleteConfig(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['simplefin-config'] })
      alert('SimpleFIN disconnected')
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to disconnect SimpleFIN')
    },
  })

  const copyInviteCode = () => {
    if (household?.invite_code) {
      navigator.clipboard.writeText(household.invite_code)
      alert('Invite code copied!')
    }
  }

  if (loadingHousehold) {
    return <div className="text-center py-8">Loading...</div>
  }

  // Show pending invitations if user has any
  if (pendingInvitations.length > 0 && !household) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Pending Invitations
            </CardTitle>
            <CardDescription>
              You have been invited to join a household
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingInvitations.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div>
                  <p className="font-medium">{invite.household_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Invited by {invite.inviter_name}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => acceptInvitation.mutate(invite.id)}
                    disabled={acceptInvitation.isPending}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => declineInvitation.mutate(invite.id)}
                    disabled={declineInvitation.isPending}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Decline
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <NoHouseholdView
          householdName={householdName}
          setHouseholdName={setHouseholdName}
          joinCode={joinCode}
          setJoinCode={setJoinCode}
          createHousehold={createHousehold}
          joinHousehold={joinHousehold}
        />
      </div>
    )
  }

  // No household - show create/join options
  if (!household) {
    return (
      <NoHouseholdView
        householdName={householdName}
        setHouseholdName={setHouseholdName}
        joinCode={joinCode}
        setJoinCode={setJoinCode}
        createHousehold={createHousehold}
        joinHousehold={joinHousehold}
      />
    )
  }

  const isAdmin = household.role === 'admin'

  return (
    <div className="space-y-6">
      {/* Household Info */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {household.name}
              </CardTitle>
              <CardDescription>
                {members.length} member{members.length !== 1 ? 's' : ''}
                {isAdmin && <Badge className="ml-2" variant="secondary">Admin</Badge>}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Invite Code */}
          <div>
            <Label className="text-sm text-muted-foreground">Invite Code</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="px-3 py-2 bg-muted rounded-md font-mono text-lg">
                {household.invite_code}
              </code>
              <Button variant="outline" size="icon" onClick={copyInviteCode}>
                <Copy className="h-4 w-4" />
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => regenerateCode.mutate()}
                  disabled={regenerateCode.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Share this code with others to let them join your household
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {member.user_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {member.user_name}
                      {member.role === 'admin' && (
                        <Crown className="h-4 w-4 text-yellow-500" />
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">{member.user_email}</p>
                  </div>
                </div>
                {isAdmin && member.role !== 'admin' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setMemberToRemove(member)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Join Requests (Admin only) */}
      {isAdmin && pendingMembers.length > 0 && (
        <Card className="border-yellow-200 dark:border-yellow-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              Pending Join Requests
              <Badge variant="secondary">{pendingMembers.length}</Badge>
            </CardTitle>
            <CardDescription>
              These users want to join your household
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingMembers.map((member) => (
                <div
                  key={member.user_id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-yellow-50 dark:bg-yellow-950"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-yellow-200 dark:bg-yellow-800 flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {member.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                      {member.requested_at && (
                        <p className="text-xs text-muted-foreground">
                          Requested {new Date(member.requested_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveMember.mutate(member.user_id)}
                      disabled={approveMember.isPending}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <UserCheck className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => rejectMember.mutate(member.user_id)}
                      disabled={rejectMember.isPending}
                    >
                      <UserX className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Member (Admin only) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Invite Member
            </CardTitle>
            <CardDescription>
              Send an invitation to someone's email address
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (inviteEmail.trim()) {
                  inviteMember.mutate(inviteEmail.trim())
                }
              }}
              className="flex gap-2"
            >
              <Input
                type="email"
                placeholder="email@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={inviteMember.isPending || !inviteEmail.trim()}>
                {inviteMember.isPending ? 'Sending...' : 'Send Invite'}
              </Button>
            </form>

            {/* Pending Invitations */}
            {sentInvitations.filter(i => i.status === 'pending').length > 0 && (
              <div className="mt-4">
                <Label className="text-sm text-muted-foreground">Pending Invitations</Label>
                <div className="space-y-2 mt-2">
                  {sentInvitations
                    .filter(i => i.status === 'pending')
                    .map((invite) => (
                      <div
                        key={invite.id}
                        className="flex items-center justify-between p-2 bg-muted rounded"
                      >
                        <span className="text-sm">{invite.email}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => cancelInvitation.mutate(invite.id)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SimpleFIN Bank Sync (Admin only) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Bank Sync (SimpleFIN)
            </CardTitle>
            <CardDescription>
              Connect your bank accounts to automatically sync transactions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {simplefinConfig?.configured ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="text-sm font-medium">SimpleFIN Connected</span>
                </div>
                {simplefinConfig.lastSynced && (
                  <p className="text-xs text-muted-foreground">
                    Last synced: {new Date(simplefinConfig.lastSynced).toLocaleString()}
                  </p>
                )}
                <Button
                  variant="outline"
                  onClick={() => deleteSimplefinConfig.mutate()}
                  disabled={deleteSimplefinConfig.isPending}
                  className="w-full"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleteSimplefinConfig.isPending ? 'Disconnecting...' : 'Disconnect SimpleFIN'}
                </Button>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (simplefinAccessUrl.trim()) {
                    setSimplefinConfig.mutate(simplefinAccessUrl.trim())
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="simplefin-url">SimpleFIN Setup Token or Access URL</Label>
                  <Input
                    id="simplefin-url"
                    type="password"
                    placeholder="Paste setup token or access URL..."
                    value={simplefinAccessUrl}
                    onChange={(e) => setSimplefinAccessUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste your setup token or access URL from{' '}
                    <a
                      href="https://beta.simplefin.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      SimpleFIN Bridge
                    </a>
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={setSimplefinConfig.isPending || !simplefinAccessUrl.trim()}
                  className="w-full"
                >
                  {setSimplefinConfig.isPending ? 'Connecting...' : 'Connect SimpleFIN'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => setShowLeaveDialog(true)}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Leave Household
          </Button>

          {isAdmin && (
            <Button
              variant="destructive"
              className="w-full justify-start"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Household
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Leave Dialog */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave Household?</DialogTitle>
            <DialogDescription>
              You will no longer have access to shared bills, transactions, and other
              household data. Your personal data will remain accessible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>Cancel</Button>
            <Button
              onClick={() => {
                leaveHousehold.mutate()
                setShowLeaveDialog(false)
              }}
            >
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Household?</DialogTitle>
            <DialogDescription>
              This will remove all members from the household. Shared data will remain
              but will no longer be associated with any household.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteHousehold.mutate()
                setShowDeleteDialog(false)
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member?</DialogTitle>
            <DialogDescription>
              Remove {memberToRemove?.user_name} from the household? They will lose
              access to shared data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberToRemove(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (memberToRemove) {
                  removeMember.mutate(memberToRemove.user_id)
                }
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Component for users without a household
function NoHouseholdView({
  householdName,
  setHouseholdName,
  joinCode,
  setJoinCode,
  createHousehold,
  joinHousehold,
}: {
  householdName: string
  setHouseholdName: (v: string) => void
  joinCode: string
  setJoinCode: (v: string) => void
  createHousehold: any
  joinHousehold: any
}) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Create a Household
          </CardTitle>
          <CardDescription>
            Start a new household to share bills and expenses with family or roommates
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (householdName.trim()) {
                createHousehold.mutate(householdName.trim())
              }
            }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="household-name">Household Name</Label>
              <Input
                id="household-name"
                placeholder="e.g., Smith Family, Apartment 4B"
                value={householdName}
                onChange={(e) => setHouseholdName(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              disabled={createHousehold.isPending || !householdName.trim()}
            >
              {createHousehold.isPending ? 'Creating...' : 'Create Household'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">Or</span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Join a Household
          </CardTitle>
          <CardDescription>
            Enter an invite code to join an existing household
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (joinCode.trim()) {
                joinHousehold.mutate(joinCode.trim())
              }
            }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="join-code">Invite Code</Label>
              <Input
                id="join-code"
                placeholder="Enter invite code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                className="font-mono"
              />
            </div>
            <Button
              type="submit"
              disabled={joinHousehold.isPending || !joinCode.trim()}
            >
              {joinHousehold.isPending ? 'Joining...' : 'Join Household'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
